/**
 * UnlockScreen — Redeems a license token from a gift / deep link.
 *
 * UI-INV-005: Leaf component, props only.
 * M2-UX-P1-011: handles /?token= deep-link activation.
 *
 * States: activating → success | error
 */
import { useEffect, useRef, useState } from "react";
import styles from "./UnlockScreen.module.css";

type Phase = "activating" | "success" | "error" | "already-used";

type Props = {
  token: string;
  onRedeemToken: (token: string) => Promise<{ success: boolean }>;
  onContinue: () => void;
};

export function UnlockScreen({ token, onRedeemToken, onContinue }: Props) {
  const firedRef = useRef(false);
  const [phase, setPhase] = useState<Phase>("activating");

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    onRedeemToken(token)
      .then(({ success }) => {
        setPhase(success ? "success" : "already-used");
      })
      .catch(() => {
        setPhase("error");
      });
  }, [token, onRedeemToken]);

  return (
    <div className={styles.unlock}>
      <div className={styles.eyebrow}>Retreat Conductor</div>

      {phase === "activating" && (
        <>
          <div className={styles.title}>Activating…</div>
          <div className={styles.subtitle}>Just a moment.</div>
          <div className={styles.spinner} aria-label="Loading" />
        </>
      )}

      {phase === "success" && (
        <>
          <div className={styles.title}>Access unlocked.</div>
          <div className={styles.subtitle}>Your retreat awaits.</div>
          <button
            className={styles.cta}
            type="button"
            onClick={onContinue}
            autoFocus
          >
            Begin
          </button>
        </>
      )}

      {phase === "already-used" && (
        <>
          <div className={styles.title}>Link already used.</div>
          <div className={styles.subtitle}>
            This link has already been redeemed. If you believe this is an
            error, please contact support.
          </div>
          <button className={styles.cta} type="button" onClick={onContinue}>
            Continue
          </button>
        </>
      )}

      {phase === "error" && (
        <>
          <div className={styles.title}>Something went wrong.</div>
          <div className={styles.subtitle}>
            The link didn't work. Try it again or contact support.
          </div>
          <button className={styles.cta} type="button" onClick={onContinue}>
            Continue
          </button>
        </>
      )}
    </div>
  );
}
